import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferPage from 'components/OfferPage'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchBestOfferForProperty, fetchOfferById } from 'actions/OfferActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'LuxKit/Typography/BodyText'
import HttpStatus from 'components/Common/HttpStatus'
import OfferPageHeadData from 'components/OfferPage/OfferPageHeadData'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'

export function getPropertyIdFromURL(propertyNameAndId: string) {
  const position = Math.max(propertyNameAndId.lastIndexOf('le:'), propertyNameAndId.lastIndexOf('bedbank:'))
  return propertyNameAndId.slice(position)
}

function PropertyPage() {
  const intl = useIntl()
  const { propertyNameAndId = '' } = useParams<'propertyNameAndId'>()
  const propertyId = useMemo(() => getPropertyIdFromURL(propertyNameAndId), [propertyNameAndId])
  const {
    bestOfferId,
    fetching,
    offer,
    currentRegionCode,
  } = useAppSelector(state => {
    const bestOfferState = state.offer.bestPropertyOffer[propertyId]
    const offer = (bestOfferState?.offerId ? state.offer.offers[bestOfferState?.offerId] : undefined) as App.AnyOffer | undefined

    return {
      bestOfferId: bestOfferState?.offerId,
      fetching: bestOfferState?.fetching ?? true,
      offer,
      currentRegionCode: state.geo.currentRegionCode,
    }
  }, shallowEqual)
  const dispatch = useAppDispatch()

  const searchingMessage = intl.formatMessage({
    id: 'CdF5FNx8nPJ0J4Foi6NX',
    defaultMessage: 'Searching for offers',
    description: 'Loading message shown while searching for property offers',
  })

  useEffect(() => {
    dispatch(fetchBestOfferForProperty(propertyId))
  }, [propertyId, dispatch])

  useEffect(() => {
    if (bestOfferId) {
      dispatch(fetchOfferById(bestOfferId))
    }
  }, [bestOfferId, dispatch])

  if (isBedbankOffer(offer)) {
    return <RedirectWithStatus code={301} to={`/${currentRegionCode.toLowerCase()}/partner/${offer.slug}/${offer.id}`} />
  }

  if (fetching) {
    return <LayoutContainer>
      <LoadingIndicator inline>
        <BodyText variant="large" weight="bold">{searchingMessage}</BodyText>
      </LoadingIndicator>
    </LayoutContainer>
  } else if (!bestOfferId) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  return <>
    {offer && isLEHotel(offer) && <OfferPageHeadData offer={offer} />}
    <OfferPage propertyPageOfferId={bestOfferId} />
  </>
}

export default PropertyPage
